import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProfileCardContract } from '../../../../app/_shared/educationProfile';
import { YoutubeVideoPreview } from '../../../../app/components/YoutubeVideo';
import { timezoneOptionLabel } from '../../../../helpers/timezone.helper';
import { PictureByGender } from '../../../user/ui/components/PictureByGender';
import { useGetPublicStudentBySlugQuery } from '../../infrastructure/api/public-profile.api';
import { ProfileReadValue } from '../components/ProfileReadValue';
import { PublicProfileView } from './PublicProfileView';

export const PublicStudentProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { slug = '' } = useParams();
  const { data, isLoading, isError } = useGetPublicStudentBySlugQuery(slug, {
    skip: !slug,
  });
  const profile = data?.profile;
  const languages = (profile?.nativeLanguages ?? [])
    .map((code) => t(`student-profile.lang.${code}`))
    .join(', ');
  const categories = (data?.categories ?? []).map((item) => item.title).join(', ');
  const card: ProfileCardContract = {
    surface: 'read',
    identity: {
      displayName: profile?.displayName ?? '',
      timezoneLabel: timezoneOptionLabel(profile?.timezone) || profile?.timezone || '',
      title: t('dashboard.profileCard.student_title'),
      skuLabel: t('student-profile.sku'),
      sku: data?.sku,
      picture: (
        <div className="profile-wrapper__picture-avatar">
          <PictureByGender picture={profile?.avatarUrl} gender={null} />
        </div>
      ),
    },
    progress: null,
  };

  return (
    <PublicProfileView
      isLoading={isLoading}
      isMissing={isError || !profile}
      card={card}
      title={t('student-profile.description')}
      publicPath={data?.publicPath}
    >
      <section className="form-section form-section--basic">
        <h2 className="form-section__title">
          {t('student-profile.steps.basic')}
        </h2>
        <ProfileReadValue
          label={t('student-profile.display_name')}
          value={profile?.displayName}
        />
        <ProfileReadValue
          label={t('student-profile.bio')}
          value={profile?.bio}
        />
      </section>

      <section className="form-section form-section--experience">
        <h2 className="form-section__title">
          {t('student-profile.steps.goals')}
        </h2>
        <ProfileReadValue
          label={t('student-profile.goals')}
          value={profile?.goals}
        />
      </section>

      <section className="form-section form-section--skills">
        <h2 className="form-section__title">
          {t('student-profile.steps.categories')}
        </h2>
        {categories ? (
          <p className="profile-read-value__text">{categories}</p>
        ) : (
          <p className="form-section__placeholder">
            {t('student-profile.categories_empty')}
          </p>
        )}
      </section>

      <section className="form-section form-section--languages">
        <h2 className="form-section__title">
          {t('student-profile.steps.languages')}
        </h2>
        <ProfileReadValue
          label={t('student-profile.native_languages')}
          value={languages || null}
        />
      </section>

      <section className="form-section form-section--media">
        <h2 className="form-section__title">
          {t('student-profile.attachments')}
        </h2>
        <div className="form-section__grid form-section__grid--2">
          <YoutubeVideoPreview
            url={profile?.introVideoUrl}
            title={t('student-profile.intro_video_title')}
          />
          <ProfileReadValue
            label={t('student-profile.timezone')}
            value={timezoneOptionLabel(profile?.timezone) || profile?.timezone}
          />
        </div>
      </section>
    </PublicProfileView>
  );
};

export default PublicStudentProfilePage;
